import { observable, action, observe, computed, makeObservable } from 'mobx'
import { IPersistObject, persistService } from '../../../services'
import { collectionListStore } from '../collection-list/collection-list.store'
import { IProduct } from '../../product'
import { ReorderProduct } from '../collections.data'
import { retrieveFromWindow } from '../../../utils/react.utils'
import { parseEntities } from '../../../utils'
import debounce from 'lodash.debounce'
import { SortingOptions } from '../../../enums/sorting-options.enums'

export interface IFilter {
  title: string
  checked?: boolean
  children?: IFilter[]
  value?: string | RegExp
}

export interface IFilterData {
  title: string
  checked: string
  group: string
  value: string
}

export interface FilterTypes {
  cuisine: string[]
  preference: string[]
  dietary_preferences: string[]
  protein: string[]
}

class FilterBarStore {
  private readonly persistKey: keyof IPersistObject = 'activeSortBy'
  @observable filterValue: string = ''
  @observable searchTerm: string = ''
  @observable activeSortBy: SortingOptions = SortingOptions.DEFAULT
  @observable productFilters: IFilter[] = this.filters
  @observable cuisineFilterOptions: string[] = []
  @observable selectedCuisineFilter: string[] = []
  @observable filterOptions: FilterTypes = {
    cuisine: [],
    preference: [],
    dietary_preferences: [],
    protein: []
  }
  @observable selectedFiltersV2: FilterTypes = {
    cuisine: [],
    preference: [],
    dietary_preferences: [],
    protein: []
  }

  constructor() {
    makeObservable(this);

    this.activeSortBy = persistService.get(this.persistKey) as SortingOptions

    // Write active activeSortBy to localStorage on change
    observe(this, 'activeSortBy', () => {
      persistService.set(this.persistKey, this.activeSortBy)
    })

    // Set products based on the change of product filters
    observe(this, 'productFilters', () => {
      const displayedProducts = this.getFilteredSearchedProducts()

      collectionListStore.setDisplayedProducts(displayedProducts)
    })

    const debounceSearchProducts = debounce(() => {
      const displayedProducts = this.getFilteredSearchedProducts()

      collectionListStore.setDisplayedProducts(displayedProducts)
    }, 330)

    observe(this, 'selectedFiltersV2', debounceSearchProducts)

    observe(this, 'searchTerm', () => debounceSearchProducts)
  }

  @computed
  get filters(): IFilter[] {
    const filterData: IFilterData[] = retrieveFromWindow('filterOptionsConfig', [])
    const filters: IFilter[] = []
    // convert settings to filter options
    filterData.forEach((filterDataObj) => {
      if (filterDataObj.group.length === 0) {
        filters.push({
          title: parseEntities(filterDataObj.title),
          value: parseEntities(filterDataObj.value),
          checked: filterDataObj.checked === 'true'
        })
      } else {
        let existingFilterGroup = filters.find((filter) => filter.title === filterDataObj.group && !!filter.children)
        if (!existingFilterGroup) {
          existingFilterGroup = {
            title: parseEntities(filterDataObj.group),
            children: []
          }
          filters.push(existingFilterGroup)
        }

        existingFilterGroup.children.push({
          title: parseEntities(filterDataObj.title),
          value: parseEntities(filterDataObj.value),
          checked: filterDataObj.checked === 'true'
        })
      }
    })

    return filters
  }

  getFilteredSearchedProducts() {
    const shouldFilter = Object.values(this.selectedFiltersV2).some((filter) => filter.length > 0)
    const filteredItems = shouldFilter ? this.getFilteredProducts(collectionListStore.products) : collectionListStore.products

    const searchedItems = this.searchTerm
      ? filteredItems.filter(
          (item) =>
            (item.name && item.name.toLowerCase().indexOf(this.searchTerm.toLowerCase()) > -1) ||
            (!!item.sku && item.sku.toLowerCase().indexOf(this.searchTerm.toLowerCase()) > -1)
        )
      : filteredItems

    return searchedItems
  }

  getFilteredSearchedReorderProducts(reorderItems: ReorderProduct[]): ReorderProduct[] {
    const filteredItems = this.selectedFilters.length > 0 ? this.getFilteredReorderProducts(reorderItems) : reorderItems

    const searchedItems = this.searchTerm
      ? filteredItems.filter(
          (reorderItem) =>
            reorderItem.product.name.toLowerCase().indexOf(this.filterValue) > -1 ||
            reorderItem.product.sku.toLowerCase().indexOf(this.filterValue) > -1
        )
      : filteredItems

    return searchedItems
  }

  @action
  handleSearchFilter = (event: React.ChangeEvent<HTMLInputElement>): void => {
    this.filterValue = event.target.value.toLowerCase()
    this.searchTerm = event.target.value

    const displayedProducts = this.getFilteredSearchedProducts()

    collectionListStore.setDisplayedProducts(displayedProducts)
  }

  setFilterState(filters: IFilter[], title?: string): IFilter[] {
    return filters.map((filter) => {
      if (filter.children) {
        // loop through children
        return {
          title: filter.title,
          value: filter.value,
          children: this.setFilterState(filter.children, title ? title : undefined)
        }
      } else {
        if (!title) {
          // reset
          return {
            title: filter.title,
            value: filter.value,
            checked: false
          }
        } else {
          if (filter.title === title) {
            // toggle
            return {
              title,
              value: filter.value,
              checked: !filter.checked
            }
          }
          return filter // no change
        }
      }
    })
  }

  getSelectedFilters(filters: IFilter[], initialValue: IFilter[]): IFilter[] {
    return filters.reduce((acc, cur): IFilter[] => {
      if (cur.children) {
        return this.getSelectedFilters(cur.children, acc)
      }

      cur.checked ? acc.push(cur) : acc

      return acc
    }, initialValue)
  }

  @computed
  get selectedFilters() {
    return this.getSelectedFilters(this.productFilters, [])
  }

  getNumOfSelectedFilters(filters: IFilter[], initialNumber: number): number {
    return filters.reduce((acc, cur) => {
      if (cur.children) {
        return this.getNumOfSelectedFilters(cur.children, acc)
      }

      return cur.checked ? acc + 1 : acc
    }, initialNumber)
  }

  getFilteredProducts(products: IProduct[]): IProduct[] {
    let filteredProducts = products
    if (this.selectedFiltersV2.cuisine.length > 0) {
      filteredProducts = filteredProducts.filter((product) =>
        this.selectedFiltersV2.cuisine.some((cuisine) => cuisine === product.metafield_cuisine)
      )
    }
    if (this.selectedFiltersV2.preference.length > 0) {
      filteredProducts = filteredProducts.filter((product) =>
        product.metafield_preference.some((preference) => this.selectedFiltersV2.preference.some((item) => preference === item))
      )
    }
    if (this.selectedFiltersV2.dietary_preferences.length > 0) {
      filteredProducts = filteredProducts.filter((product) =>
        product.metafield_category.some((category) => this.selectedFiltersV2.dietary_preferences.some((item) => category === item))
      )
    }
    if (this.selectedFiltersV2.protein.length > 0) {
      filteredProducts = filteredProducts.filter((product) => this.selectedFiltersV2.protein.includes(product.metafield_protein))
    }
    return filteredProducts
  }

  getFilteredReorderProducts(products: ReorderProduct[]): ReorderProduct[] {
    return products.filter((reorderProduct: ReorderProduct) => {
      const filterInTags = this.selectedFilters.every(
        (filter) => !!reorderProduct.product.tags.find((tag) => !!tag.toLowerCase().match(filter.value))
      )
      return filterInTags
    })
  }
  
  organizeFilters(products: IProduct[]): void {
    const NoneValue = 'None'
    const cuisineFilter = new Set<string>()
    const preferenceFilter = new Set<string>()
    const categoryFilter = new Set<string>()
    const proteinFilter = new Set<string>()
    for (const product of products) {
      if (product.metafield_cuisine) {
        cuisineFilter.add(product.metafield_cuisine)
      }
      if (product.metafield_preference) {
        product.metafield_preference.forEach((preference) => {
          if (preference !== NoneValue) {
            preferenceFilter.add(preference)
          }
        })
      }
      if (product.metafield_category) {
        product.metafield_category.forEach((category) => {
          if (category !== NoneValue) {
            categoryFilter.add(category)
          }
        })
      }
      if (product.metafield_protein && product.metafield_protein !== NoneValue) {
        proteinFilter.add(product.metafield_protein)
      }
    }
    this.cuisineFilterOptions = Array.from(cuisineFilter)

    this.filterOptions = {
      cuisine: Array.from(cuisineFilter),
      preference: Array.from(preferenceFilter),
      dietary_preferences: Array.from(categoryFilter),
      protein: Array.from(proteinFilter)
    }
    this.selectedFiltersV2 = {
      cuisine: [],
      preference: [],
      dietary_preferences: [],
      protein: []
    }
  }

  @action
  setProductFilters = (title?: string): void => {
    this.productFilters = this.setFilterState(this.productFilters, title)
  }

  @action
  resetProductFilters = (): void => {
    this.setProductFilters()
  }

  @computed
  get numberOfSelectedProductFilters(): number {
    return this.getNumOfSelectedFilters(this.productFilters, 0)
  }

  @action
  setSelectedCuisineFilters = (cuisineFilterOptions: string[]): void => {
    this.selectedCuisineFilter = cuisineFilterOptions
  }

  @action setSelectedFiltersV2 = (type: keyof FilterTypes, filters: string[]): void => {
    this.selectedFiltersV2 = {
      ...this.selectedFiltersV2,
      [type]: filters
    }
  }

  @action removeSelectedFilter = (type: keyof FilterTypes, filter: string): void => {
    this.selectedFiltersV2 = {
      ...this.selectedFiltersV2,
      [type]: this.selectedFiltersV2[type].filter((item) => item !== filter)
    }
  }

  @action resetSelectedFiltersV2 = (): void => {
    this.selectedFiltersV2 = {
      cuisine: [],
      preference: [],
      dietary_preferences: [],
      protein: []
    }
  }
}

export const filterBarStore = new FilterBarStore()
